/** @jsx jsx */
import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, css } from '@emotion/react';
import { type RenderFn, type ForgeDoc } from '@atlassian/forge-ui-types';
import { token } from '@atlaskit/tokens';

export interface ListProps {
	type: 'ordered' | 'unordered';
	children: React.ReactNode;
}

// bullet style ordering
const UNORDERED_LIST_STYLES = ['disc', 'circle', 'square'];
const ORDERED_LIST_STYLES = ['decimal', 'lower-alpha', 'lower-roman'];

// create a type ForgeDocWihData that includes an indentLevel object, to avoid exposing indentLevel prop in ListProps
type ForgeDocWithData = Omit<ForgeDoc, 'children'> & {
	data: { indentLevel: number };
	children: ForgeDocWithData[];
};

export const List = (props: Parameters<RenderFn>[0]) => {
	const { forgeDoc, render } = props;
	const { type } = props.forgeDoc.props as ListProps;

	const forgeDocWithData = forgeDoc as ForgeDocWithData;
	// initialise indentLevel to 0 and add to forgeDocWithData
	if (forgeDocWithData.data === undefined) {
		forgeDocWithData.data = { indentLevel: 0 };
	}
	const { indentLevel } = forgeDocWithData.data;

	// set the list style according to the list type
	let listType;
	type === 'ordered' ? (listType = ORDERED_LIST_STYLES) : (listType = UNORDERED_LIST_STYLES);

	// set list style
	let currentIndentLevel = indentLevel;
	let currentStyle = listType[currentIndentLevel % listType.length];

	// assign the child indentLevel
	let childIndentLevel = (currentIndentLevel + 1) % listType.length;

	// pass iterated indentLevel to child
	forgeDocWithData.children.forEach((child) => {
		if (child.type === 'List') {
			child.data = { ...child.data, indentLevel: childIndentLevel };
		}
	});

	const listChildren = forgeDocWithData.children.map((child) => render(child));
	const cssStyle = css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		listStyleType: currentStyle,
		lineHeight: token('space.300'),
		marginLeft: token('space.negative.200'), // indentation spacing is 24px
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'> *:not(:first-of-type)': {
			// throws console error when first-child is used, using first-of-type instead
			marginTop: token('space.050'),
		},
	});

	if (type === 'ordered') {
		return (
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			<ol css={cssStyle} key={forgeDocWithData.key}>
				{listChildren}
			</ol>
		);
	}
	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
		<ul css={cssStyle} key={forgeDocWithData.key}>
			{listChildren}
		</ul>
	);
};
